import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Chip,
  Stack,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CircularProgress,
  InputBase,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  QueryStats as QueryIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  EmojiEvents as EventsIcon,
  Search as SearchIcon,
  Lock as LockIcon,
  InsertDriveFileOutlined as FileIcon,
} from '@mui/icons-material';

import AdminWrapper from '../components/layout/AdminWrapper';
import { fetchAvailableSeasons, fetchSeasonalResults, getDefaultSeason } from '../components/data/dataQueries';
import { usePlayerData } from '../components/data/DataContext';
import { gridSpacing } from '../assets/theme/muitheme';
import { DS, Overline, Panel, PageHeader, enterSx, numericSx } from '../components/shared/Dashboard';

interface QueryResult {
  headers: string[];
  rows: any[][];
  metadata?: {
    totalRows: number;
    executionTime: number;
    queryType: string;
  };
}

interface QueryParams {
  type: string;
  season?: string;
  playerLimit?: number;
  outputFilename?: string;
  eventTypes?: string[];
}

// Query types that only have placeholder data so far
const COMING_SOON = ['event_statistics', 'player_rankings'];

const AdminDatabaseQueries: React.FC = () => {
  const context = usePlayerData();

  const [queryParams, setQueryParams] = useState<QueryParams>({
    type: '',
  });
  const [results, setResults] = useState<QueryResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [seasons, setSeasons] = useState<{ value: string; label: string }[]>([]);
  const [loadingSeasons, setLoadingSeasons] = useState(true);
  const [resultFilter, setResultFilter] = useState('');
  const [sort, setSort] = useState<{ column: number; direction: 'asc' | 'desc' } | null>(null);

  // Check if user has admin access
  const isAdmin = context?.isAdmin || false;

  // Predefined query types
  const queryTypes = [
    {
      value: 'seasonal_results',
      label: 'Seasonal Results',
      description: 'Get comprehensive results for specific seasons',
      icon: <TrendingUpIcon fontSize="small" />,
    },
    {
      value: 'event_statistics',
      label: 'Event Statistics',
      description: 'Analyze event participation and performance metrics',
      icon: <EventsIcon fontSize="small" />,
    },
    {
      value: 'player_rankings',
      label: 'Player Rankings',
      description: 'Top players by rating, participation, or achievements',
      icon: <AssessmentIcon fontSize="small" />,
    },
  ];

  // Load available seasons from Firebase once on mount
  useEffect(() => {
    const loadSeasons = async () => {
      try {
        setLoadingSeasons(true);
        const availableSeasons = await fetchAvailableSeasons();
        setSeasons(availableSeasons);

        // Set default season if none selected
        if (availableSeasons.length > 0) {
          const defaultSeason = await getDefaultSeason();
          setQueryParams(prev => (prev.season ? prev : { ...prev, season: defaultSeason }));
        }
      } catch (error) {
        console.error('Failed to load seasons:', error);
        setError('Failed to load seasons from database');
      } finally {
        setLoadingSeasons(false);
      }
    };

    loadSeasons();
  }, []);

  const playerLimitOptions = [
    { value: undefined, label: 'All Players' },
    { value: 25, label: 'Top 25 Players' },
    { value: 50, label: 'Top 50 Players' },
    { value: 100, label: 'Top 100 Players' },
    { value: 250, label: 'Top 250 Players' },
    { value: 500, label: 'Top 500 Players' },
  ];

  const eventTypeOptions = [
    { value: 'tournament', label: 'Tournaments' },
    { value: 'league', label: 'League Play' },
    { value: 'casual', label: 'Casual Rounds' },
    { value: 'practice', label: 'Practice Rounds' },
  ];

  const handleParamChange = (field: keyof QueryParams, value: any) => {
    setQueryParams(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const executeSeasonalQuery = async (): Promise<QueryResult> => {
    try {
      if (!queryParams.season) {
        throw new Error('No season selected');
      }

      // Fetch real data from Firebase using the dataQueries utility
      console.log(`Fetching seasonal results for ${queryParams.season} with limit: ${queryParams.playerLimit || 'none'}`);

      const startedAt = performance.now();
      const playersData = await fetchSeasonalResults(queryParams.season, queryParams.playerLimit);
      const executionTime = Number(((performance.now() - startedAt) / 1000).toFixed(2));

      return {
        headers: ['PID', 'Name', 'Rating', 'Games Count', 'Division', 'Points'],
        rows: playersData.map(player => [
          player.pid,
          player.name,
          player.rating,
          player.gamesCount,
          player.division,
          player.points
        ]),
        metadata: {
          totalRows: playersData.length,
          executionTime,
          queryType: `${queryParams.season} - ${queryParams.playerLimit ? `Top ${queryParams.playerLimit}` : 'All'} Players`,
        },
      };
    } catch (error) {
      console.error('Error in executeSeasonalQuery:', error);
      throw new Error(`Failed to fetch seasonal data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const executeQuery = async () => {
    setLoading(true);
    setError(null);
    setResultFilter('');
    setSort(null);

    try {
      let mockResults: QueryResult;

      switch (queryParams.type) {
        case 'seasonal_results':
          mockResults = await executeSeasonalQuery();
          break;

        case 'event_statistics':
          mockResults = {
            headers: ['Event Name', 'Date', 'Participants', 'Avg Rating', 'Course', 'Event Type'],
            rows: [
              ['Spring Championship', '2024-04-15', 45, 912, 'Pine Valley DGC', 'Tournament'],
              ['Weekly League #12', '2024-04-08', 28, 885, 'Oak Ridge Park', 'League'],
              ['Summer Series #3', '2024-06-22', 38, 898, 'Meadow Brook', 'Tournament'],
              ['Casual Friday Round', '2024-07-05', 16, 875, 'River Bend', 'Casual'],
            ],
            metadata: {
              totalRows: 4,
              executionTime: 0.8,
              queryType: 'Event Statistics',
            },
          };
          break;

        case 'player_rankings':
          mockResults = {
            headers: ['Rank', 'Player Name', 'Current Rating', 'Rounds Played', 'Win Rate', 'Avg Score'],
            rows: [
              [1, 'Alex Turner', 955, 67, '28%', -2.3],
              [2, 'Emma Davis', 948, 52, '25%', -1.8],
              [3, 'Chris Martinez', 942, 71, '22%', -1.5],
              [4, 'Rachel Green', 938, 48, '21%', -1.2],
              [5, 'Tom Anderson', 935, 63, '19%', -0.9],
            ],
            metadata: {
              totalRows: 5,
              executionTime: 0.6,
              queryType: 'Player Rankings',
            },
          };
          break;

        default:
          mockResults = {
            headers: ['Column 1', 'Column 2', 'Column 3'],
            rows: [['Sample', 'Data', 'Row']],
            metadata: {
              totalRows: 1,
              executionTime: 0.1,
              queryType: 'Sample Query',
            },
          };
      }

      setResults(mockResults);
    } catch (err) {
      setError('Failed to execute query. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generatedFilename = queryParams.type === 'seasonal_results'
    ? (queryParams.outputFilename
      ? (queryParams.outputFilename.endsWith('.csv') ? queryParams.outputFilename : `${queryParams.outputFilename}.csv`)
      : `${queryParams.season?.toLowerCase() || 'season'}_${queryParams.playerLimit ? `top${queryParams.playerLimit}` : 'all'}_${new Date().toISOString().split('T')[0]}.csv`)
    : `${queryParams.type}_${new Date().toISOString().split('T')[0]}.csv`;

  const exportResults = () => {
    if (!results) return;

    // Convert results to CSV format
    const csvContent = [
      results.headers.join(','),
      ...results.rows.map(row => row.map(cell => {
        // Escape cells that contain commas or quotes
        if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"'))) {
          return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = generatedFilename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Filter + sort only change what is shown; the export always contains the full result
  const visibleRows = useMemo(() => {
    if (!results) return [];
    const query = resultFilter.trim().toLowerCase();
    let rows = query
      ? results.rows.filter(row => row.some(cell => String(cell ?? '').toLowerCase().includes(query)))
      : results.rows;

    if (sort) {
      rows = [...rows].sort((a, b) => {
        const x = a[sort.column];
        const y = b[sort.column];
        const bothNumbers = typeof x === 'number' && typeof y === 'number';
        const cmp = bothNumbers ? x - y : String(x ?? '').localeCompare(String(y ?? ''), undefined, { numeric: true });
        return sort.direction === 'asc' ? cmp : -cmp;
      });
    }
    return rows;
  }, [results, resultFilter, sort]);

  const toggleSort = (column: number) => {
    setSort(prev => {
      if (prev?.column !== column) return { column, direction: 'desc' };
      if (prev.direction === 'desc') return { column, direction: 'asc' };
      return null;
    });
  };

  if (!isAdmin) {
    return (
      <AdminWrapper>
        <Box sx={{ textAlign: 'center', py: 8, px: 3, background: DS.surface, border: `1px solid ${DS.border}`, borderRadius: DS.radius }}>
          <LockIcon sx={{ fontSize: 32, color: DS.warning, mb: 1 }} />
          <Typography sx={{ fontWeight: 800, fontSize: '18px', color: DS.text }}>
            Access Denied
          </Typography>
          <Typography sx={{ fontSize: '14px', color: DS.textMuted, mt: 0.5, maxWidth: 480, mx: 'auto' }}>
            You do not have permission to access the Queries section. This feature is restricted to administrators only.
          </Typography>
        </Box>
      </AdminWrapper>
    );
  }

  const isComingSoon = COMING_SOON.includes(queryParams.type);

  return (
    <AdminWrapper>
      <Grid container spacing={gridSpacing} sx={{ mb: 6 }}>
        <PageHeader
          overline="Admin"
          title="Database queries"
          subtitle="Run custom queries to analyze seasonal results, event statistics, and player performance data."
        />

        {/* Query type selection */}
        {queryTypes.map((queryType, index) => {
          const selected = queryParams.type === queryType.value;
          const soon = COMING_SOON.includes(queryType.value);

          return (
            <Grid item xs={12} sm={4} key={queryType.value} sx={enterSx(0.05 * (index + 1))}>
              <Box
                role="button"
                aria-pressed={selected}
                onClick={() => setQueryParams(prev => ({ ...prev, type: queryType.value }))}
                sx={{
                  height: '100%',
                  p: 2,
                  borderRadius: DS.radius,
                  cursor: 'pointer',
                  background: selected ? alpha(DS.brand, 0.08) : DS.surface,
                  border: `1px solid ${selected ? alpha(DS.brand, 0.5) : DS.border}`,
                  opacity: soon && !selected ? 0.7 : 1,
                  transition: 'all 0.15s ease',
                  '&:hover': { borderColor: alpha(DS.brand, 0.35), opacity: 1 },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box sx={{ color: selected ? DS.brand : DS.textMuted, display: 'flex', p: 0.75, borderRadius: '10px', background: selected ? alpha(DS.brand, 0.14) : DS.inset }}>
                    {queryType.icon}
                  </Box>
                  {soon && (
                    <Box component="span" sx={{ px: 1, py: '2px', borderRadius: '999px', background: DS.inset, color: DS.textMuted, fontSize: '11px', fontWeight: 700 }}>
                      Coming soon
                    </Box>
                  )}
                </Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: DS.text }}>{queryType.label}</Typography>
                <Typography sx={{ fontSize: '13px', color: DS.textMuted, mt: 0.25 }}>{queryType.description}</Typography>
              </Box>
            </Grid>
          );
        })}

        {/* Query parameters */}
        {queryParams.type && (
          <Panel
            title="Query parameters"
            subtitle={isComingSoon ? 'This query type is not available yet' : undefined}
            delay={0.2}
          >
            <Grid container spacing={2}>
              {/* Seasonal Results Parameters */}
              {queryParams.type === 'seasonal_results' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Season</InputLabel>
                      <Select
                        value={queryParams.season || ''}
                        onChange={(e) => handleParamChange('season', e.target.value)}
                        label="Season"
                        disabled={loadingSeasons}
                      >
                        {loadingSeasons ? (
                          <MenuItem disabled>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            Loading seasons...
                          </MenuItem>
                        ) : (
                          seasons.map((season) => (
                            <MenuItem key={season.value} value={season.value}>
                              {season.label}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Player Limit</InputLabel>
                      <Select
                        value={queryParams.playerLimit || ''}
                        onChange={(e) => handleParamChange('playerLimit', e.target.value === '' ? undefined : Number(e.target.value))}
                        label="Player Limit"
                      >
                        {playerLimitOptions.map((option, index) => (
                          <MenuItem key={index} value={option.value || ''}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Custom Output Filename (Optional)"
                      placeholder="e.g., season14_top25_custom.csv"
                      value={queryParams.outputFilename || ''}
                      onChange={(e) => handleParamChange('outputFilename', e.target.value)}
                      helperText="Leave empty to auto-generate filename based on season and limit"
                    />
                  </Grid>
                  {/* Filename Preview */}
                  {queryParams.season && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: '10px', background: DS.inset }}>
                        <FileIcon sx={{ fontSize: 16, color: DS.textFaint }} />
                        <Typography sx={{ fontSize: '13px', color: DS.textMuted }}>
                          Exports as{' '}
                          <Box component="span" sx={{ fontFamily: 'monospace', color: DS.text, fontWeight: 600 }}>
                            {generatedFilename}
                          </Box>
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </>
              )}

              {/* Event Statistics Parameters */}
              {queryParams.type === 'event_statistics' && (
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Event Types</InputLabel>
                    <Select
                      multiple
                      value={queryParams.eventTypes || []}
                      onChange={(e) => handleParamChange('eventTypes', e.target.value)}
                      label="Event Types"
                      renderValue={(selected) => (
                        <Stack direction="row" spacing={1}>
                          {(selected as string[]).map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Stack>
                      )}
                    >
                      {eventTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Actions */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pt: 0.5 }}>
                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <QueryIcon />}
                    onClick={executeQuery}
                    disabled={loading || isComingSoon}
                    sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 700, px: 2.5 }}
                  >
                    {loading ? 'Executing...' : isComingSoon ? 'Feature Coming Soon' : 'Execute Query'}
                  </Button>
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                      setResults(null);
                      setError(null);
                    }}
                    disabled={loading}
                    sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 700, color: DS.textMuted }}
                  >
                    Clear Results
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Panel>
        )}

        {/* Error Display */}
        {error && (
          <Grid item xs={12}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        )}

        {/* Results Display */}
        {results && (
          <Panel
            title="Query results"
            subtitle={results.metadata && (
              <Box component="span" sx={numericSx}>
                {results.metadata.queryType} · {results.metadata.totalRows} rows · {results.metadata.executionTime}s
              </Box>
            )}
            action={
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={exportResults}
                sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 700 }}
              >
                Export CSV
              </Button>
            }
            delay={0}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                flex: { xs: '1 1 100%', sm: '0 1 280px' },
                background: DS.inset,
                border: `1px solid ${DS.border}`,
                borderRadius: '10px',
                '&:focus-within': { borderColor: alpha(DS.brand, 0.5) },
              }}>
                <SearchIcon sx={{ fontSize: 18, color: DS.textFaint }} />
                <InputBase
                  value={resultFilter}
                  onChange={(e) => setResultFilter(e.target.value)}
                  placeholder="Filter results"
                  inputProps={{ 'aria-label': 'Filter results' }}
                  sx={{ flex: 1, fontSize: '14px', color: DS.text, py: 0.5 }}
                />
              </Box>
              <Overline>
                {resultFilter.trim() ? `${visibleRows.length} of ${results.rows.length} shown` : 'Click a column to sort'}
              </Overline>
            </Box>

            <TableContainer sx={{ maxHeight: 600, borderRadius: '12px', border: `1px solid ${DS.border}` }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {results.headers.map((header, index) => (
                      <TableCell
                        key={index}
                        sortDirection={sort?.column === index ? sort.direction : false}
                        sx={{
                          background: DS.surface,
                          borderBottom: `1px solid ${DS.borderStrong}`,
                          fontSize: '11px',
                          fontWeight: 700,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          color: DS.textMuted,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <TableSortLabel
                          active={sort?.column === index}
                          direction={sort?.column === index ? sort.direction : 'desc'}
                          onClick={() => toggleSort(index)}
                        >
                          {header}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.map((row, rowIndex) => (
                    <TableRow key={rowIndex} hover sx={{ '&:hover': { background: `${DS.inset} !important` } }}>
                      {row.map((cell, cellIndex) => (
                        <TableCell
                          key={cellIndex}
                          sx={{
                            fontSize: '13px',
                            color: cellIndex === 1 ? DS.text : DS.textMuted,
                            fontWeight: cellIndex === 1 ? 600 : 400,
                            borderBottom: `1px solid ${DS.border}`,
                            ...(typeof cell === 'number' ? numericSx : {}),
                          }}
                        >
                          {cell}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {visibleRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={results.headers.length} sx={{ textAlign: 'center', py: 4, color: DS.textMuted, borderBottom: 'none' }}>
                        No rows match "{resultFilter.trim()}".
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Panel>
        )}
      </Grid>
    </AdminWrapper>
  );
};

export default AdminDatabaseQueries;
